/**
 * L.15.5 — NIPE tool: openSpecializationLab
 *
 * UI navigation tool. Asks the SPA to open the Specialization Lab
 * window (`/specialization-lab` in the WindowContext registry).
 *
 * Per L.15 D4: NIPE may *open* the Lab and *describe* its controls,
 * but never twist knobs. The user owns the sliders.
 */

import type { NipeTool } from './types';

export interface OpenSpecializationLabArgs {
  /** Optional skill name (matches `skillMarketplace.name`) to land on
   * in the Lab's header picker. */
  skillName?: string;
  /** Optional probe text to seed the prompt textarea, overriding the
   * skill's curated probe. */
  probeText?: string;
}

export interface OpenSpecializationLabContext {
  /** Optional. Only present when the SPA wires the WindowContext into
   * the NIPE runner (everywhere except headless fixtures). */
  openSpecializationLab?: (
    args: OpenSpecializationLabArgs,
  ) => Promise<{ opened: boolean }>;
}

export const openSpecializationLabTool: NipeTool<OpenSpecializationLabContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'openSpecializationLab',
      description:
        'Open the Specialization Lab window. Use when the user wants to see what a specialization (LoRA) is doing to outputs, tune the training hyperparameters, or kick a re-specialize run. Pairs with `describeSpecializationControls` to walk the user through what the knobs do. Pass `skillName` to pre-select a specific skill in the Lab header (must match `skillMarketplace.name`). Pass `probeText` to seed the probe prompt textarea with a custom string — useful when the user asks "try this prompt".',
      parameters: {
        type: 'object',
        properties: {
          skillName: {
            type: 'string',
            description:
              'Optional. Skill name to pre-select in the Lab header picker.',
          },
          probeText: {
            type: 'string',
            description:
              'Optional. Probe prompt text to seed the textarea; overrides the skill\'s curated probe.',
          },
        },
        required: [],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: false,
  handler: async (args, ctx) => {
    if (!ctx.openSpecializationLab) {
      return {
        error:
          'Specialization Lab integration is not available in this session.',
      };
    }
    const labArgs: OpenSpecializationLabArgs = {};
    if (args && typeof args === 'object') {
      const a = args as { skillName?: unknown; probeText?: unknown };
      if (typeof a.skillName === 'string') labArgs.skillName = a.skillName;
      if (typeof a.probeText === 'string') labArgs.probeText = a.probeText;
    }
    return await ctx.openSpecializationLab(labArgs);
  },
};
